import { TimeZone } from "client/DateAndTime/timezones";
import {
	MouseEvent,
	ReactElement,
	useMemo
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	generateTrackingId,
	isNilOrWhiteSpace,
	isNotNil,
	isNotNilOrWhiteSpace
} from "client/Misc/utils";
import {
	Anchor,
	DateString
} from "client/Components";
import { SectionGrid } from "./SectionGrid";
import {
	EmailClient,
	IEmailClientComposeFeature,
	getEffectiveEmailClient
} from "client_legacy/Email/emailClient";
import { DateFormat, formatDate } from "client_legacy/ndaw/dateFormatting";
import { Section, SectionEmptyBehavior } from "./Section";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { convertToDateTime } from "client/DateAndTime/dateUtils";
import { UserProfileCustomStringViewModel } from "client/Api/UserProfile/service";

export type PhoneConfiguration = {
	mobilePhone?: string | null;
	mobilePhone2?: string | null;
	businessPhone?: string | null;
	businessPhone2?: string | null;
	homePhone?: string | null;
	homePhone2?: string | null;
};

export type GeneralInformationSectionProps = {
	birthday: DateOnly | null;
	emailAddresses: readonly string[];
	phoneNumbers: PhoneConfiguration;
	timezone: TimeZone;
	emptyBehavior: SectionEmptyBehavior;
	isCopyToClipboardEnabled?: boolean;
	areContactLinksEnabled?: boolean;
	shouldShowBirthYear?: boolean;
	customString1?: OptionalModel<UserProfileCustomStringViewModel>;
	customString2?: OptionalModel<UserProfileCustomStringViewModel>;
	customString3?: OptionalModel<UserProfileCustomStringViewModel>;
	customString4?: OptionalModel<UserProfileCustomStringViewModel>;
	customString5?: OptionalModel<UserProfileCustomStringViewModel>;
};

export const GeneralInformationSection = (props: GeneralInformationSectionProps): ReactElement => {
	const {
		birthday,
		emailAddresses,
		phoneNumbers,
		timezone,
		emptyBehavior,
		isCopyToClipboardEnabled = false,
		areContactLinksEnabled = false,
		shouldShowBirthYear = false,
		customString1,
		customString2,
		customString3,
		customString4,
		customString5
	} = props;

	const {
		mobilePhone,
		mobilePhone2,
		businessPhone,
		businessPhone2,
		homePhone,
		homePhone2
	} = phoneNumbers;

	const hasAPhoneNumber: boolean = [
		mobilePhone,
		mobilePhone2,
		businessPhone,
		businessPhone2,
		homePhone,
		homePhone2
	]
		.some(isNotNilOrWhiteSpace);

	const renderPhoneNumber = (
		phoneNumber: string | nil,
		titleKey: LocalizationKey,
		trackingId: string
	): ReactElement | null => {
		if (isNotNilOrWhiteSpace(phoneNumber)) {
			return (
				<PhoneNumber
					phoneNumber={phoneNumber}
					areContactLinksEnabled={areContactLinksEnabled}
					isCopyToClipboardEnabled={isCopyToClipboardEnabled}
					titleKey={titleKey}
					trackingId={trackingId}
				/>
			);
		}

		return null;
	};

	const emailClient: EmailClient = useMemo(
		() => getEffectiveEmailClient(),
		[]
	);

	const emailClickHandler = (
		event: MouseEvent,
		emailClient: IEmailClientComposeFeature,
		emailAddress: string
	): void => {
		event.preventDefault();
		emailClient.composeEmail({to: emailAddress});
	};

	const birthdayDateTime = useMemo(
		(): Date | null => {
			if (!birthday) {
				return null;
			}

			return convertToDateTime(
				birthday,
				timezone
			);
		},
		[birthday, timezone]
	);

	const birthdayFormat: DateFormat = shouldShowBirthYear
		? "DMY"
		: "DM";

	const hasCustomStrings: boolean = [
		customString1,
		customString2,
		customString3,
		customString4,
		customString5
	]
		.some(
			x => x?.isSet
				&& isNotNilOrWhiteSpace(x.value.value)
		);

	return (
		<Section
			title={localize("_GeneralInformation")}
			trackingId="general-information"
			emptyBehavior={emptyBehavior}
			isEmpty={
				!birthday
					&& emailAddresses.length === 0
					&& !hasAPhoneNumber
					&& !hasCustomStrings
			}
		>
			<SectionGrid>
				{isNotNil(birthdayDateTime) && (
					<SectionGrid.Row
						icon="cake-candles"
						label={localize("_Birthday")}
						trackingId="birthday"
					>
						<SectionGrid.Value
							valueToCopy={
								isCopyToClipboardEnabled
									? formatDate(
										birthdayDateTime,
										birthdayFormat,
										timezone
									)
									: undefined
							}
						>
							<DateString
								value={birthdayDateTime}
								format={birthdayFormat}
								timezone={timezone}
							/>
						</SectionGrid.Value>
					</SectionGrid.Row>
				)}
				{emailAddresses.length > 0 && (
					<SectionGrid.Row
						icon="envelope"
						label={localize("_EmailAddress")}
						trackingId="email-address"
					>
						{emailAddresses.map(
							((emailAddress, index) => (
								<SectionGrid.Value
									key={index}
									valueToCopy={
										isCopyToClipboardEnabled
											? emailAddress
											: null
									}
								>
									{areContactLinksEnabled && emailClient.supportsEmailCompose
										? (
											<Anchor
												onClick={(event) => emailClickHandler(event, emailClient, emailAddress)}
												href={`mailto:${emailAddress}`}
											>
												{emailAddress}
											</Anchor>
										)
										: emailAddress
									}
								</SectionGrid.Value>
							))
						)}
					</SectionGrid.Row>
				)}
				{hasAPhoneNumber && (
					<SectionGrid.Row
						icon="phone"
						label={localize("_PhoneNumber")}
						trackingId="phone-number-container"
					>
						{renderPhoneNumber(mobilePhone, "_Mobile", "mobile-phone")}
						{renderPhoneNumber(mobilePhone2, "_Mobile2", "mobile-phone2")}
						{renderPhoneNumber(homePhone, "_Home", "home-phone")}
						{renderPhoneNumber(homePhone2, "_Home2", "home-phone2")}
						{renderPhoneNumber(businessPhone, "_Work", "business-phone")}
						{renderPhoneNumber(businessPhone2, "_Work2", "business-phone2")}
					</SectionGrid.Row>
				)}


				{customString1?.isSet && (
					<CustomString
						customStringIndex={1}
						model={customString1.value}
						isCopyToClipboardEnabled={isCopyToClipboardEnabled}
					/>
				)}

				{customString2?.isSet && (
					<CustomString
						customStringIndex={2}
						model={customString2.value}
						isCopyToClipboardEnabled={isCopyToClipboardEnabled}
					/>
				)}

				{customString3?.isSet && (
					<CustomString
						customStringIndex={3}
						model={customString3.value}
						isCopyToClipboardEnabled={isCopyToClipboardEnabled}
					/>
				)}

				{customString4?.isSet && (
					<CustomString
						customStringIndex={4}
						model={customString4.value}
						isCopyToClipboardEnabled={isCopyToClipboardEnabled}
					/>
				)}

				{customString5?.isSet && (
					<CustomString
						customStringIndex={5}
						model={customString5.value}
						isCopyToClipboardEnabled={isCopyToClipboardEnabled}
					/>
				)}
			</SectionGrid>
		</Section>
	);
};
GeneralInformationSection.displayName = "GeneralInformationSection";

type PhoneNumberProps = {
	phoneNumber: string;
	titleKey: LocalizationKey;
	trackingId: string;
	isCopyToClipboardEnabled: boolean;
	areContactLinksEnabled: boolean;
};

const PhoneNumber = (props: PhoneNumberProps): ReactElement => {
	const {
		areContactLinksEnabled,
		isCopyToClipboardEnabled,
		phoneNumber,
		titleKey,
		trackingId
	} = props;

	return (
		<SectionGrid.Value
			label={localize(titleKey)}
			trackingId={trackingId}
			valueToCopy={
				isCopyToClipboardEnabled
					? phoneNumber
					: null
			}
		>
			{areContactLinksEnabled
				? <Anchor href={`tel:${phoneNumber}`}>{phoneNumber}</Anchor>
				: phoneNumber
			}
		</SectionGrid.Value>
	);
};
PhoneNumber.displayName = "PhoneNumber";

type CustomStringProps = {
	customStringIndex: number;
	model: UserProfileCustomStringViewModel;
	isCopyToClipboardEnabled: boolean;
};

const CustomString = (props: CustomStringProps): ReactElement | null => {
	const {
		customStringIndex,
		model,
		isCopyToClipboardEnabled
	} = props;

	const {
		displayName,
		value
	} = model;

	if (isNilOrWhiteSpace(value)) {
		return null;
	}

	return (
		<SectionGrid.Row
			label={displayName}
			trackingId={generateTrackingId("custom-string", `|${customStringIndex}`)}
		>
			<SectionGrid.Value
				valueToCopy={
					isCopyToClipboardEnabled
						? value
						: null
				}
			>
				{value}
			</SectionGrid.Value>
		</SectionGrid.Row>
	);
};
CustomString.displayName = "CustomString";